import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { FileLabel } from "shared/types/file";
import { ExtractedContractDto } from "./types";

export interface ExtractionContext {
  companyId: number;
  unitId?: string;
  buildingId?: string;
  userId?: string;
}

/** A document reference the split-view PDF preview can render. */
export interface PreviewDocument {
  sharePointFileId?: string;
  sharePointFileUrl?: string;
  name?: string;
}

export interface UploadedHuurcontract extends PreviewDocument {
  fileLogId: number;
}

/**
 * Upload a Huurcontract document via the standard /file/log pipeline (so it is
 * stored in SharePoint and labelled "Huurcontract"), returning the new fileLog
 * id plus its SharePoint references (for the PDF preview). Uploaded
 * synchronously so the bytes are available for immediate analysis.
 */
export async function uploadHuurcontract(
  file: File,
  ctx: ExtractionContext,
  session: any,
): Promise<UploadedHuurcontract> {
  const form = new FormData();
  form.append("file", file);
  form.append(
    "fileLog",
    JSON.stringify({
      fileLabel: FileLabel.Huurcontract,
      createdByUserId: ctx.userId,
      buildingId: ctx.buildingId,
      unitId: ctx.unitId,
    }),
  );

  const response = await fetchWithAuth(
    `${API_URL}/file/log`,
    { method: "POST", body: form },
    session,
  );

  if (!response.ok) {
    throw new Error(`Uploaden van het huurcontract is mislukt (status ${response.status}).`);
  }

  const result = await response.json();
  const savedFileLog = result?.fileLog;
  const fileLogId = savedFileLog?.id;
  if (!fileLogId) {
    throw new Error("Het geüploade huurcontract kon niet worden geïdentificeerd.");
  }
  return {
    fileLogId,
    sharePointFileId: savedFileLog?.sharePointFileId,
    sharePointFileUrl: savedFileLog?.sharePointFileUrl,
    name: file.name,
  };
}

/**
 * Run Ella over an already-stored Huurcontract (uploaded or picked) and return
 * the prefilled-contract payload.
 */
export async function analyzeContract(
  fileLogId: number,
  ctx: ExtractionContext,
  session: any,
): Promise<ExtractedContractDto> {
  const response = await fetchWithAuth(
    `${API_URL}/rental/contracts/extract-from-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileLogId,
        companyId: ctx.companyId,
        unitId: ctx.unitId,
        buildingId: ctx.buildingId,
        userId: ctx.userId,
      }),
    },
    session,
  );

  if (!response.ok) {
    throw new Error(`Analyse van het huurcontract is mislukt (status ${response.status}).`);
  }

  return (await response.json()) as ExtractedContractDto;
}
